import React from "react";
import { motion } from "framer-motion";

const formatAppointment = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-GB", options).replace(",", "");
};

const PrescriptionPreview = ({
  patientName,
  date,
  symptoms,
  diagnosis,
  medicines,
  labTests,
  sos,
  notes,
  nextAppointment,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl shadow-md p-5 border border-emerald-100 text-sm sm:text-base hover:shadow-lg transition"
    >
      <h2 className="text-lg sm:text-xl font-bold text-emerald-700 text-center mb-4">
        🩺 Digital Prescription
      </h2>

      {/* Patient Info and SOS */}
      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-4 text-gray-700">
        <div>
          <p>
            <span className="font-semibold">👤 Patient Name:</span> {patientName}
          </p>
          <p className="mt-1">
            <span className="font-semibold">📅 Date:</span> {date}
          </p>
        </div>
        <div>
          <p>
            <span className="font-semibold">🚨 SOS:</span> {sos || "N/A"}
          </p>
        </div>
      </div>

      <div className="border-t border-gray-200 mb-4"></div>

      {/* Symptoms */}
      <div className="mb-4">
        <h3 className="font-semibold text-emerald-600">🩹 Symptoms</h3>
        <p className="text-gray-800">{symptoms || "N/A"}</p>
      </div>

      {/* Diagnosis */}
      <div className="mb-4">
        <h3 className="font-semibold text-emerald-600">🧠 Diagnosis</h3>
        <p className="text-gray-800">{diagnosis || "N/A"}</p>
      </div>

      {/* Medicines Table */}
      <div className="mb-4">
        <h3 className="font-semibold text-emerald-600 mb-1">💊 Medicines</h3>
        <div className="overflow-x-auto rounded-lg">
          <table className="min-w-full bg-emerald-50 rounded-lg overflow-hidden text-xs sm:text-sm">
            <thead className="bg-emerald-100">
              <tr>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Dosage</th>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">Days</th>
              </tr>
            </thead>
            <tbody>
              {medicines && medicines.length > 0 ? (
                medicines.map((med, index) => (
                  <tr key={index} className="border-t">
                    <td className="p-2">{med.name || "-"}</td>
                    <td className="p-2">{med.dosage || "-"}</td>
                    <td className="p-2">{med.time || "-"}</td>
                    <td className="p-2">{med.days || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-2 text-center">
                    No medicines prescribed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lab Tests */}
      <div className="mb-4">
        <h3 className="font-semibold text-emerald-600">🧪 Lab Tests</h3>
        <ul className="list-disc list-inside text-gray-800">
        {labTests && labTests.filter(Boolean).length > 0 ? (
  labTests.map((test, index) => {
    // Convert "{'name': 'CBC', ...}" to valid JSON
    const parsed = JSON.parse(test.replace(/'/g, '"'));
    return <li key={index}>{parsed.name}</li>;
  })) : (
            <li>No lab tests recommended.</li>
          )}
        </ul>
      </div>

      {/* Additional Notes */}
      {notes && (
        <div className="mb-4">
          <h3 className="font-semibold text-emerald-600">📝 Additional Notes</h3>
          <p className="text-gray-800">{notes}</p>
        </div>
      )}

      {/* Next Appointment */}
      {nextAppointment && (
        <div>
          <h3 className="font-semibold text-emerald-600">📆 Next Appointment</h3>
          <p className="text-gray-800">{formatAppointment(nextAppointment)}</p>
        </div>
      )}
    </motion.div>
  );
};

export default PrescriptionPreview;
